import {
  Editor,
  rootCtx,
  defaultValueCtx,
  editorViewOptionsCtx,
  editorViewCtx,
  serializerCtx,
} from '@milkdown/core';
import { parseFrontmatter, type FrontmatterParse } from './frontmatter';
import { installBrokenImageFallback } from './broken-image-fallback';
import { commonmark, headingIdGenerator } from '@milkdown/preset-commonmark';
import { gfm } from '@milkdown/preset-gfm';
import '@milkdown/prose/view/style/prosemirror.css';
import type { EditorView } from '@milkdown/prose/view';
import type { Transaction } from '@milkdown/prose/state';
import './style.css';
import showcase from './fixtures/commonmark-showcase.md?raw';
import prdTemplate from './templates/prd.md?raw';
import visionTemplate from './templates/vision.md?raw';
import taskTemplate from './templates/task.md?raw';
import { listen } from '@tauri-apps/api/event';
import { open as openDialog, save as saveDialog } from '@tauri-apps/plugin-dialog';
import { invoke } from '@tauri-apps/api/core';
import { parseSpecUrl } from './router';
import { fetchSpec } from './fetch-spec';
import { renderLanding } from './landing';
import { renderViewerHeader } from './viewer-header';
import { renderViewerError } from './viewer-error';
import { mountViewer } from './viewer';
import { attemptEditAction, PENDING_EDIT_KEY } from './edit-mode';
import { mountSessionIndicator } from './session-indicator';

const TEMPLATES: Record<'prd' | 'vision' | 'task', string> = {
  prd: prdTemplate,
  vision: visionTemplate,
  task: taskTemplate,
};

export type TemplateKind = keyof typeof TEMPLATES;

export type EditorMode = 'read' | 'edit';

export async function mountEditor(
  host: HTMLElement,
  content: string,
  mode: EditorMode = 'read',
): Promise<Editor> {
  // Per-mount arming flag for dirty tracking. The dispatchTransaction
  // wrapper closes over this so transactions Milkdown/ProseMirror fire
  // during initial setup (before the mount is "settled") cannot flip
  // dirty. We arm it on the next macrotask after `.create()` resolves —
  // by then the plugin lifecycle has played out and any further
  // docChanged transaction is user-driven.
  const armRef = { armed: false };
  const editor = await Editor.make()
    .config((ctx) => {
      ctx.set(rootCtx, host);
      ctx.set(defaultValueCtx, content);
      ctx.update(editorViewOptionsCtx, (prev) =>
        mode === 'edit'
          ? {
              ...prev,
              editable: () => true,
              attributes: { 'aria-readonly': 'false', 'tabindex': '0' },
              // Issue #7 / #33: install dirty-tracking dispatchTransaction
              // wrapper ONLY in edit mode. Read-mode mounts get NO wrapper,
              // so a programmatic `view.dispatch(tr)` (which ProseMirror
              // does not block even when `editable: () => false`) cannot
              // signal dirty — first defensive layer against the #33
              // write-handle bypass.
              dispatchTransaction(this: EditorView, tr: Transaction) {
                this.updateState(this.state.apply(tr));
                if (armRef.armed && tr.docChanged) {
                  markDirty();
                }
              },
            }
          : {
              ...prev,
              editable: () => false,
              attributes: { 'aria-readonly': 'true', 'tabindex': '0' },
            },
      );
      const seenHeadingIds = new Map<string, number>();
      const nodeIdCache = new WeakMap<object, string>();
      ctx.set(headingIdGenerator.key, (node) => {
        const cached = nodeIdCache.get(node);
        if (cached) return cached;
        if (node.attrs?.id) {
          nodeIdCache.set(node, node.attrs.id);
          return node.attrs.id;
        }
        const base = node.textContent.toLowerCase().trim().replace(/\s+/g, '-');
        const count = seenHeadingIds.get(base) ?? 0;
        seenHeadingIds.set(base, count + 1);
        const id = count === 0 ? base : `${base}-${count}`;
        nodeIdCache.set(node, id);
        return id;
      });
    })
    .use(commonmark)
    .use(gfm)
    .create();
  if (mode === 'edit') {
    // Arm on the next macrotask. Mount-time setup transactions resolve
    // synchronously / in microtasks during `.create()`; a 0-ms timer is
    // a coarse-but-reliable boundary between "mount setup" and "user
    // edits".
    setTimeout(() => {
      armRef.armed = true;
    }, 0);
  }
  installBrokenImageFallback(host);
  return editor;
}

// Issue #78 slice 1 — broken-image alt-text fallback. The implementation
// lives in `./broken-image-fallback` so the web-mode `mountViewer` can
// share it without dragging in this module's Tauri imports.

let dragDropGuardInstalled = false;

export function installDragDropGuard(target: Window | Document = window): void {
  if (dragDropGuardInstalled) return;
  dragDropGuardInstalled = true;
  const stop = (e: Event) => {
    e.preventDefault();
  };
  target.addEventListener('dragover', stop);
  target.addEventListener('drop', stop);
}

export interface FileOpened {
  path: string;
  name: string;
  content: string;
}

// Issue #43 — Sanitize filenames against Unicode RTL / zero-width /
// control codepoints before they land in `document.title` (or any
// other UI surface that could be spoofed). Strips:
//
//   - U+0000..U+001F  C0 controls
//   - U+007F..U+009F  DEL + C1 controls
//   - U+200B..U+200F  zero-width chars + LRM/RLM bidi marks
//   - U+202A..U+202E  bidi embedding/override codepoints
//   - U+2066..U+2069  bidi isolate codepoints
//
// Plain ASCII, accented Latin, and CJK pass through unchanged — the
// stripped ranges target invisible / formatting codepoints, not
// legitimate filename content.
const FILENAME_STRIP_RE =
  /[ --​-‏‪-‮⁦-⁩]/g;

export function sanitizeFilename(name: string): string {
  return name.replace(FILENAME_STRIP_RE, '');
}

// Issue #48 — Frontmatter recognition is implemented in `./frontmatter`
// and re-exported here so existing call sites (`parseFrontmatter` /
// `FrontmatterParse`) keep their import path. The extraction keeps the
// parser usable from web-mode (`src/viewer.ts`) without dragging
// `main.ts`'s Tauri side-effects.
export { parseFrontmatter, type FrontmatterParse };

let currentEditor: Editor | null = null;
let currentEditorMode: EditorMode = 'read';
let currentEditorHost: HTMLElement | null = null;
let currentFileName: string | null = null;
// Issue #7: tracks the on-disk path of the currently open file so Cmd+S
// can save in place. Null when no file is open OR for template-new
// buffers (slice 14 / #49) which route Cmd+S through Save-As instead.
let currentFilePath: string | null = null;
// Issue #48: raw frontmatter block including both `---` fences. Saved
// verbatim ahead of the editor-serialized body so the round-trip is
// byte-equal (no YAML serializer involved). Null when the opened doc
// has no frontmatter or the YAML failed to parse.
let currentFrontmatter: string | null = null;

export function getCurrentEditor(): Editor | null {
  return currentEditor;
}

// Issue #7: dirty-bit + title-bullet state. The bit flips true on any
// edit-mode transaction with `docChanged` (see mountEditor's
// dispatchTransaction wrapper) and resets to false on file-open.
let dirty = false;

export function isDirty(): boolean {
  return dirty;
}

function syncDirtyUi(): void {
  if (typeof document === 'undefined') return;
  const base = currentFileName ? `${currentFileName} — Hashly` : 'Hashly';
  document.title = dirty ? `• ${base}` : base;
}

function markDirty(): void {
  if (dirty) return;
  dirty = true;
  syncDirtyUi();
}

function clearDirty(): void {
  dirty = false;
  syncDirtyUi();
}

let toggleInFlight = false;

async function toggleEditMode(): Promise<void> {
  if (toggleInFlight) return;
  const editor = currentEditor;
  const host = currentEditorHost;
  if (!editor || !host) return;
  toggleInFlight = true;
  if (editToggleButton) editToggleButton.disabled = true;
  try {
    let md: string;
    try {
      md = editor.action((ctx) => {
        const view = ctx.get(editorViewCtx);
        const serializer = ctx.get(serializerCtx);
        return serializer(view.state.doc);
      });
    } catch {
      return;
    }
    const nextMode: EditorMode = currentEditorMode === 'read' ? 'edit' : 'read';
    try {
      await editor.destroy();
    } catch {
      /* swallow — destroy may reject if the editor was already torn down */
    }
    currentEditor = null;
    host.innerHTML = '';
    // Issue #48 cross-slice fix: `host.innerHTML = ''` wipes the
    // frontmatter panel that handleFileOpened / newFromTemplate
    // appended. Re-render it from `currentFrontmatter` so the
    // metadata stays visible across mode toggles. Re-parse from the
    // raw block (which is preserved verbatim for byte-equal save).
    if (currentFrontmatter !== null) {
      const reparsed = parseFrontmatter(currentFrontmatter);
      if (reparsed.parsed) {
        renderFrontmatterPanel(host, reparsed.parsed);
      }
    }
    let next: Editor;
    try {
      next = await mountEditor(host, md, nextMode);
    } catch (e) {
      // Issue #36 / #37: mountEditor rejection inside toggleEditMode
      // used to leave a blank pane + enabled button + stale label.
      // Now we render a user-visible error and keep the toggle UI
      // honest — no editor is mounted, so the label / aria reflect
      // a state the user can act on (the file-error surface stands
      // in for a doc).
      console.error('[hashly] toggleEditMode: mountEditor rejected', e);
      currentEditorMode = 'read';
      syncEditToggleUi();
      renderFileError(
        host,
        "Couldn't switch modes — the document failed to mount.",
      );
      return;
    }
    currentEditor = next;
    currentEditorMode = nextMode;
    currentEditorHost = host;
    syncEditToggleUi();
    if (nextMode === 'edit') {
      next.action((ctx) => {
        const view = ctx.get(editorViewCtx);
        view.focus();
      });
    } else if (editToggleButton) {
      // Re-enable BEFORE focus — jsdom (per HTML spec) refuses to focus
      // a disabled element. The `finally` block re-enables idempotently.
      editToggleButton.disabled = false;
      editToggleButton.focus();
    }
  } finally {
    toggleInFlight = false;
    if (editToggleButton) editToggleButton.disabled = false;
  }
}

let editToggleInstalled = false;
let editToggleButton: HTMLButtonElement | null = null;

const EDIT_TOGGLE_LABEL_READ = 'Edit';
const EDIT_TOGGLE_LABEL_EDIT = 'Read';

function syncEditToggleUi(): void {
  if (!editToggleButton) return;
  editToggleButton.setAttribute(
    'aria-pressed',
    currentEditorMode === 'edit' ? 'true' : 'false',
  );
  editToggleButton.textContent =
    currentEditorMode === 'edit' ? EDIT_TOGGLE_LABEL_EDIT : EDIT_TOGGLE_LABEL_READ;
}

function installEditToggle(): void {
  if (editToggleInstalled) return;
  if (typeof document === 'undefined') return;
  editToggleInstalled = true;
  const button = document.createElement('button');
  button.type = 'button';
  button.setAttribute('data-testid', 'edit-toggle');
  button.setAttribute('aria-pressed', 'false');
  button.className = 'hashly-edit-toggle';
  button.textContent = EDIT_TOGGLE_LABEL_READ;
  button.disabled = true;
  button.addEventListener('click', () => {
    void toggleEditMode();
  });
  editToggleButton = button;
  document.body.appendChild(button);
}

// Issue #35 / #37 — handleFileOpened symmetry with toggleEditMode.
// The destroy + mount sequence is the same shape; the same defenses
// (sync disable, null currentEditor before mount, re-entrancy guard,
// try/finally, render-on-error) apply.
let fileOpenInFlight = false;

export async function handleFileOpened(payload: FileOpened, host: HTMLElement): Promise<void> {
  if (fileOpenInFlight) return;
  fileOpenInFlight = true;
  if (editToggleButton) editToggleButton.disabled = true;
  try {
    if (currentEditor) {
      try {
        await currentEditor.destroy();
      } catch {
        /* swallow — destroy may reject if the editor was already torn down */
      }
    }
    // Null the reference BEFORE the new mount await. If mountEditor
    // rejects, module state stays clean (no dangling pointer to the
    // destroyed editor that subsequent .action() calls would try to
    // reach through).
    currentEditor = null;
    host.innerHTML = '';
    currentFileName = sanitizeFilename(payload.name);
    currentFilePath = payload.path;
    clearDirty();
    // Issue #48 — strip frontmatter before mounting; re-emit on save.
    const fm = parseFrontmatter(payload.content);
    currentFrontmatter = fm.frontmatter;
    if (fm.parsed) {
      renderFrontmatterPanel(host, fm.parsed);
    }
    let editor: Editor;
    try {
      editor = await mountEditor(host, fm.body);
    } catch (e) {
      console.error('[hashly] handleFileOpened: mountEditor rejected', e);
      renderFileError(
        host,
        "Couldn't open this document — the editor failed to mount.",
        currentFilePath ?? undefined,
      );
      return;
    }
    currentEditor = editor;
    currentEditorMode = 'read';
    currentEditorHost = host;
    syncEditToggleUi();
  } finally {
    fileOpenInFlight = false;
    if (editToggleButton) editToggleButton.disabled = false;
  }
}

// Issue #48 — render a metadata panel above the editor showing the
// parsed frontmatter as key/value pairs. Read-mode minimum (plain
// styling per the AC); edit-mode editing of the frontmatter via a
// fenced-code-style region is a follow-up.
function renderFrontmatterPanel(
  host: HTMLElement,
  parsed: Record<string, unknown>,
): void {
  const panel = document.createElement('aside');
  panel.setAttribute('data-testid', 'frontmatter-panel');
  panel.setAttribute('role', 'region');
  panel.setAttribute('aria-label', 'Document metadata');
  panel.className = 'hashly-frontmatter-panel';
  for (const [key, value] of Object.entries(parsed)) {
    const row = document.createElement('div');
    row.className = 'hashly-frontmatter-panel__row';
    const k = document.createElement('span');
    k.className = 'hashly-frontmatter-panel__key';
    k.textContent = key;
    const v = document.createElement('span');
    v.className = 'hashly-frontmatter-panel__value';
    v.textContent = formatFrontmatterValue(value);
    row.appendChild(k);
    row.appendChild(v);
    panel.appendChild(row);
  }
  host.appendChild(panel);
}

function formatFrontmatterValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map(formatFrontmatterValue).join(', ');
  }
  // Object — render as inline JSON. Edge-case for nested YAML; the
  // panel's job is "show the keys to the user", not pretty-print.
  try {
    return JSON.stringify(value);
  } catch {
    return '[object]';
  }
}

// Cross-slice guard: every OPEN path (File > Open, Finder
// double-click, File > New From Template) destroys the current
// editor and replaces it. If the current buffer is dirty, that
// silently discards the user's edits — the close-guard's protection
// is bypassable by any of three trivial actions. The guard wraps
// each entry point with the same Save / Don't Save / Cancel prompt.
//
// Returns `true` when the caller should proceed with the open;
// `false` when the user chose Cancel OR the save attempt failed
// (sticky-dirty after save = "the file isn't safely on disk yet,
// don't lose the edits by opening something else").
export async function guardOpenAgainstDirty(): Promise<boolean> {
  if (!isDirty()) return true;
  const choice = await confirmUnsavedClose(currentFileName);
  if (choice === 'cancel') return false;
  if (choice === 'discard') return true;
  // 'save' — try to save; only proceed if dirty actually cleared.
  await saveCurrent();
  return !isDirty();
}

// Issue #49 — slice 14: open a new unsaved buffer from a baked-in
// template. Hydrates `{{author}}` / `{{date}}` placeholders before
// mounting; opens in edit mode with dirty=true and currentFilePath=null
// so the next Cmd+S routes through Save-As.
export async function newFromTemplate(
  kind: TemplateKind,
  host: HTMLElement,
): Promise<void> {
  if (fileOpenInFlight) return;
  if (!(await guardOpenAgainstDirty())) return;
  fileOpenInFlight = true;
  if (editToggleButton) editToggleButton.disabled = true;
  try {
    const template = TEMPLATES[kind];
    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    let author = 'Author';
    try {
      const fromInvoke = await invoke<string>('get_current_user');
      if (typeof fromInvoke === 'string' && fromInvoke.length > 0) {
        author = fromInvoke;
      }
    } catch {
      // Non-Tauri / failed lookup — keep the literal fallback.
    }
    const hydrated = template
      .replace(/\{\{author\}\}/g, author)
      .replace(/\{\{date\}\}/g, today);

    if (currentEditor) {
      try {
        await currentEditor.destroy();
      } catch {
        /* swallow */
      }
    }
    currentEditor = null;
    host.innerHTML = '';
    currentFileName = `Untitled ${kind}.md`;
    currentFilePath = null; // Save-As path
    clearDirty();
    const fm = parseFrontmatter(hydrated);
    currentFrontmatter = fm.frontmatter;
    if (fm.parsed) {
      renderFrontmatterPanel(host, fm.parsed);
    }
    let editor: Editor;
    try {
      editor = await mountEditor(host, fm.body, 'edit');
    } catch (e) {
      console.error('[hashly] newFromTemplate: mountEditor rejected', e);
      renderFileError(host, "Couldn't open template — the editor failed to mount.");
      return;
    }
    currentEditor = editor;
    currentEditorMode = 'edit';
    currentEditorHost = host;
    syncEditToggleUi();
    // Mark dirty from frame zero so the user sees the bullet and the
    // close-guard catches an accidental close.
    markDirty();
  } finally {
    fileOpenInFlight = false;
    if (editToggleButton) editToggleButton.disabled = false;
  }
}

// Issue #7 — slice 2: serialize the current editor doc and write it to
// `currentFilePath` via the `save_md_file` Tauri command. On success,
// clear dirty. On failure, log and leave dirty sticky-true so the user
// still sees the title-bar bullet warning. The blocking save-failure
// dialog from spec.md is deferred until #50 (ACL extension) lands the
// `dialog:allow-message` capability.
//
// `saveCurrent` does NOT take an editor argument — it serializes the
// internal `currentEditor` handle. That's the frontend half of #33's
// write-handle-bypass hardening: no caller hands a write-capable
// handle to the save path; the save path serializes the handle the
// module already owns and ships a `&str` over IPC.
export async function saveCurrent(): Promise<void> {
  if (currentFilePath === null) {
    // Issue #49 — slice 14: Save-As routing. Cmd+S on a path-less
    // buffer (template-new) opens the native save picker. If the
    // user cancels, the buffer stays dirty + unsaved (no invoke).
    // If the user picks a path, we set currentFilePath and fall
    // through to the in-place save path below.
    let chosen: string | null;
    try {
      chosen = await saveDialog({
        filters: [{ name: 'Markdown', extensions: ['md', 'markdown'] }],
        defaultPath: currentFileName ?? 'Untitled.md',
      });
    } catch (e) {
      console.error('[hashly] saveCurrent: save dialog failed', e);
      return;
    }
    if (!chosen) return; // user cancelled — dirty stays sticky
    currentFilePath = chosen;
  }
  // Issue #33 save-time mode guard. The dirty-bit-side defense
  // (read-mode mounts get no dispatchTransaction wrapper) blocks the
  // dirty SIGNAL but not the OUTCOME — a programmatic mutation in read
  // mode (e.g. via getCurrentEditor() in devtools, or via a future UI
  // component that misuses the editor handle) leaves view state
  // mutated. Without this guard, sticky-dirty + read-mode-mutation +
  // Cmd+S would persist the read-mode mutation. The persona walkthrough
  // in spec.md shows the user-Cmd+S path explicitly happening in edit
  // mode ("they fix the line in WYSIWYG, hit Cmd+S"); refusing read-
  // mode saves matches the documented workflow. Slice 8 (#8 unsaved-
  // on-close) covers the close-with-unsaved-edits path so users don't
  // lose work; this guard only refuses the explicit read-mode Cmd+S.
  if (currentEditorMode !== 'edit') return;
  const editor = currentEditor;
  if (!editor) return;
  let md: string;
  try {
    md = editor.action((ctx) => {
      const view = ctx.get(editorViewCtx);
      const serializer = ctx.get(serializerCtx);
      return serializer(view.state.doc);
    });
  } catch (e) {
    console.error('[hashly] save: serialization failed', e);
    return;
  }
  // Issue #48 — re-emit the raw frontmatter block (verbatim, including
  // fences) ahead of the editor body so the round-trip is byte-equal.
  const finalContent = (currentFrontmatter ?? '') + md;
  try {
    await invoke('save_md_file', { path: currentFilePath, content: finalContent });
    clearDirty();
  } catch (e) {
    // Sticky-dirty on failure. The user's edits are still unsaved;
    // the title-bar bullet must keep warning them. Slice paired with
    // #50 will add a blocking dialog on top of this log line.
    console.error('[hashly] save: invoke failed', e);
  }
}

// Issue #8 — slice 3: unsaved-changes-on-close dialog. The modal lives
// in the WebView (Tauri 2's plugin-dialog only natively supports
// 2-button confirms; 3-button "Save / Don't Save / Cancel" requires a
// custom modal). The HTML5 `<dialog role="alertdialog">` element gives
// modal behavior for free + screen-reader-correct semantics.
export function confirmUnsavedClose(
  filename: string | null,
): Promise<'save' | 'discard' | 'cancel'> {
  return new Promise((resolve) => {
    const dialog = document.createElement('div');
    dialog.setAttribute('role', 'alertdialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.className = 'hashly-close-confirm';

    const message = document.createElement('p');
    message.className = 'hashly-close-confirm__message';
    const docPhrase = filename ?? 'this untitled document';
    message.textContent = `Do you want to save the changes you made in ${docPhrase}?`;
    dialog.appendChild(message);

    const buttonRow = document.createElement('div');
    buttonRow.className = 'hashly-close-confirm__buttons';

    const make = (label: string, action: 'save' | 'discard' | 'cancel') => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = label;
      btn.dataset.action = action;
      btn.addEventListener('click', () => {
        if (dialog.parentElement) dialog.parentElement.removeChild(dialog);
        resolve(action);
      });
      return btn;
    };
    // Order matches macOS-native unsaved-changes prompt:
    // Save (default) | Don't Save | Cancel.
    buttonRow.appendChild(make('Save', 'save'));
    buttonRow.appendChild(make("Don't Save", 'discard'));
    buttonRow.appendChild(make('Cancel', 'cancel'));
    dialog.appendChild(buttonRow);

    document.body.appendChild(dialog);
  });
}

// Issue #8 decision function. Pure(-ish) — all side-effects come in
// through injected deps, so the test can supply mocks. The runtime
// wiring is in `installCloseGuard` below.
export interface CloseRequestDeps {
  event: { preventDefault: () => void };
  isDirty: () => boolean;
  currentFilename: () => string | null;
  confirm: (filename: string | null) => Promise<'save' | 'discard' | 'cancel'>;
  save: () => Promise<void>;
  destroy: () => Promise<void>;
}

export async function handleCloseRequest(deps: CloseRequestDeps): Promise<void> {
  if (!deps.isDirty()) {
    // Clean: let the close proceed. No preventDefault, no destroy
    // (the OS-level close handler does its job).
    return;
  }
  deps.event.preventDefault();
  const choice = await deps.confirm(deps.currentFilename());
  if (choice === 'cancel') return; // window stays open
  if (choice === 'discard') {
    await deps.destroy();
    return;
  }
  // 'save'
  await deps.save();
  // Critical UX pin (#8 verify): only destroy if the save actually
  // cleared dirty. saveCurrent swallows invoke rejections and leaves
  // dirty sticky-true; in that case we MUST NOT close — otherwise
  // the user picks Save, the save fails, and the window closes anyway,
  // silently losing work. The save-failure dialog (paired with #50
  // ACL extension) will give the user a Save As… escape later; until
  // then, sticky-dirty + no-destroy is the safe default.
  if (!deps.isDirty()) {
    await deps.destroy();
  }
}

let closeGuardInstalled = false;

export async function installCloseGuard(): Promise<void> {
  if (closeGuardInstalled) return;
  closeGuardInstalled = true;
  // Lazy-import the Tauri window API so non-Tauri envs (Vitest+jsdom)
  // don't blow up on the import. Same pattern as the menu-open-file
  // listener in `bootstrap`.
  let getCurrentWindow: typeof import('@tauri-apps/api/window').getCurrentWindow;
  try {
    ({ getCurrentWindow } = await import('@tauri-apps/api/window'));
  } catch {
    return;
  }
  try {
    const win = getCurrentWindow();
    await win.onCloseRequested(async (event) => {
      await handleCloseRequest({
        event,
        isDirty,
        currentFilename: () => currentFileName,
        confirm: confirmUnsavedClose,
        save: saveCurrent,
        destroy: () => win.destroy(),
      });
    });
  } catch {
    // listen rejects in non-Tauri envs — acceptable.
  }
}

// Issue #27 — keyboard escape from edit mode. Tab is captured by
// ProseMirror for natural list-indent ergonomics (matches every
// markdown editor the persona has used); Escape is the explicit
// keyboard escape that blurs the editor and focuses the toggle
// button. Read mode is exempt (read-mode editor isn't the focus
// source for editing).
let escapeHandlerInstalled = false;

export function installEscapeHandler(target: Document = document): void {
  if (escapeHandlerInstalled) return;
  if (typeof document === 'undefined') return;
  escapeHandlerInstalled = true;
  target.addEventListener('keydown', (e: Event) => {
    const ev = e as KeyboardEvent;
    if (ev.key !== 'Escape') return;
    if (currentEditorMode !== 'edit') return;
    if (!editToggleButton) return;
    editToggleButton.focus();
  });
}

let saveHandlerInstalled = false;

export function installSaveHandler(target: Document = document): void {
  if (saveHandlerInstalled) return;
  if (typeof document === 'undefined') return;
  saveHandlerInstalled = true;
  target.addEventListener('keydown', (e: Event) => {
    const ev = e as KeyboardEvent;
    // Accept Cmd+S (Mac) and Ctrl+S (cross-platform). Hashly is
    // mac-only per CLAUDE.md but the test surface (jsdom) and any
    // future cross-platform contributor benefits from accepting both.
    if (ev.key !== 's') return;
    if (!ev.metaKey && !ev.ctrlKey) return;
    ev.preventDefault();
    void saveCurrent();
  });
}

export async function openFileViaDialog(host: HTMLElement): Promise<void> {
  if (!(await guardOpenAgainstDirty())) return;
  const selected = await openDialog({
    multiple: false,
    filters: [{ name: 'Markdown', extensions: ['md', 'markdown'] }],
  });
  if (!selected || Array.isArray(selected)) return;
  try {
    const payload = await invoke<FileOpened>('read_md_file', { path: selected });
    await handleFileOpened(payload, host);
  } catch (e) {
    renderFileError(host, "Can't open this file — it doesn't look like text.", String(selected));
  }
}

export function renderFileError(host: HTMLElement, message: string, path?: string): void {
  host.innerHTML = '';
  const alert = document.createElement('div');
  alert.setAttribute('role', 'alert');
  alert.className = 'hashly-file-error';
  const heading = document.createElement('p');
  heading.className = 'hashly-file-error__message';
  heading.textContent = message;
  alert.appendChild(heading);
  if (path) {
    const detail = document.createElement('p');
    detail.className = 'hashly-file-error__path';
    detail.textContent = path;
    alert.appendChild(detail);
  }
  host.appendChild(alert);
  document.title = 'Hashly';
}

export function bootstrap(): void {
  if (typeof document === 'undefined') return;
  // fix-loop iter-1 / fix #15 — gate the Tauri-flavored Cmd+S
  // handler behind `isTauri`. In web mode, the handler's
  // saveCurrent path tries to open Tauri's saveDialog (which fails
  // silently in the browser) — user pressed Cmd+S, sees no banner,
  // no PR, no feedback. The web Cmd+S path is intentionally
  // unhandled here; the Save button click is the AC 6.1 seam.
  const isTauri =
    typeof (window as unknown as { __TAURI_INTERNALS__?: unknown })
      .__TAURI_INTERNALS__ !== 'undefined';
  installDragDropGuard();
  installEditToggle();
  if (isTauri) {
    installSaveHandler();
  }
  installEscapeHandler();
  void installCloseGuard().catch(() => {
    /* non-Tauri envs — close guard is a no-op there */
  });
  void listen<void>('menu-open-file', () => {
    const editorHost = document.getElementById('editor');
    if (editorHost) {
      void openFileViaDialog(editorHost);
    }
  }).catch(() => { /* listen rejects in non-Tauri envs (e.g. plain browser / vitest) — acceptable */ });
  // Issue #5 — Finder double-click / Open With > Hashly. Rust emits
  // `file-opened-by-os` with the file path string from RunEvent::Opened
  // (see src-tauri/src/lib.rs). The frontend reads the file via the
  // same hardened `read_md_file` seam used by File > Open and routes
  // through handleFileOpened — so the security model (canonicalize +
  // allow-list, #44) applies identically to OS-initiated opens.
  // Issue #49 — File > New From Template menu wire-up. Rust emits
  // `new-from-template` with a string payload (`"prd"` / `"vision"` /
  // `"task"`); we hydrate + mount.
  void listen<string>('new-from-template', async (event) => {
    const editorHost = document.getElementById('editor');
    if (!editorHost) return;
    const kind = event.payload;
    if (kind !== 'prd' && kind !== 'vision' && kind !== 'task') return;
    await newFromTemplate(kind, editorHost);
  }).catch(() => { /* non-Tauri envs — acceptable */ });
  void listen<string>('file-opened-by-os', async (event) => {
    const editorHost = document.getElementById('editor');
    if (!editorHost) return;
    const path = event.payload;
    if (typeof path !== 'string' || !path) return;
    if (!(await guardOpenAgainstDirty())) return;
    try {
      const payload = await invoke<FileOpened>('read_md_file', { path });
      await handleFileOpened(payload, editorHost);
    } catch {
      renderFileError(
        editorHost,
        "Can't open this file — it doesn't look like text.",
        path,
      );
    }
  }).catch(() => { /* non-Tauri envs — acceptable */ });
  const host = document.getElementById('editor');
  if (!host) {
    console.warn('[hashly] #editor host element not found; mountEditor not auto-invoked');
    return;
  }
  // Issue #90 / fix #3 — env-branched bootstrap. In a Tauri runtime
  // window.__TAURI_INTERNALS__ is injected by the host before user
  // scripts run; in a plain browser it's absent. The web branch runs
  // the AC 4.1–4.7 orchestration (parseSpecUrl → fetchSpec → mountViewer
  // / renderLanding / renderViewerError); the Tauri branch keeps the
  // unchanged v0.2 showcase mount. (`isTauri` is computed at the top
  // of bootstrap() so the Cmd+S installSaveHandler gate sees the
  // same value.)
  if (!isTauri) {
    const headerHost = document.getElementById('viewer-header') ?? host;
    void bootstrapWeb(host, headerHost, window.location.href);
    return;
  }
  void mountEditor(host, showcase)
    .then((editor) => {
      currentEditor = editor;
      currentEditorMode = 'read';
      currentEditorHost = host;
      if (editToggleButton) editToggleButton.disabled = false;
    })
    .catch((e) => {
      // Issue #31 — preventive observability. The bootstrap mount is
      // reliable in practice (Milkdown's `.create()` doesn't throw on
      // valid markdown showcase fixtures), but a silent rejection
      // would leave the toggle permanently disabled with zero user
      // signal. Console-only is sufficient per the issue body — the
      // mount is dev/build infrastructure rather than user content.
      console.error('[hashly] bootstrap: showcase mountEditor rejected', e);
    });
}

// Issue #90 / fix #3 — web-mode bootstrap orchestration.
async function bootstrapWeb(
  host: HTMLElement,
  headerHost: HTMLElement,
  href: string,
): Promise<void> {
  // Issue #90 / fix #7 — strip the static `.hashly-titlebar` in web
  // mode. The web surfaces (landing / viewer-header) own their own
  // wordmark, so leaving the static titlebar visible would stack two
  // branded headers. Tauri mode never reaches this codepath, so its
  // titlebar stays.
  document.querySelectorAll('.hashly-titlebar').forEach((el) => el.remove());
  const parsed = parseSpecUrl(href);
  if ('error' in parsed) {
    renderLanding(host, parsed.error);
    return;
  }
  renderViewerHeader(headerHost, parsed);
  const result = await fetchSpec(parsed.repo, parsed.ref, parsed.path);
  if (result.ok) {
    try {
      await mountViewer(host, result.content);
    } catch (e) {
      console.error('[hashly] bootstrapWeb: mountViewer rejected', e);
      return;
    }
    document.title = `${parsed.path} — Hashly`;
    // Issue #91 fix-loop-1 / fix #1 — surface the avatar + sign-out
    // indicator after the spec successfully mounts. mountSessionIndicator
    // appends INTO the header (already painted) and fires its own
    // session-status fetch independent of the spec fetch. Fire-and-
    // forget so a slow indicator fetch doesn't block UI completion.
    void mountSessionIndicator(headerHost);
    // Issue #91 fix-loop-1 / fix #2 + fix-loop-2 / fix #1 — wire the
    // JIT auth flow to a real user-edit trigger via `keydown` (the
    // round-1 `beforeinput` shape was broken in real browsers because
    // beforeinput doesn't fire on contenteditable=false). The
    // listener filters to content-modifying keys, preventDefaults to
    // keep characters from leaking past the read-only flag, and
    // self-removes after attemptEditAction reaches enterEditMode
    // ('success') so post-flip keystrokes don't (a) fire a second
    // fetch chain per character or (b) get preventDefault'd and lost.
    const keydownHandler = (event: KeyboardEvent): void => {
      if (!isContentModifyingKey(event)) return;
      event.preventDefault();
      void (async () => {
        const result = await attemptEditAction(host);
        // Iter-3 fix #2 — self-remove on `'success'` (editor is now
        // editable; ProseMirror should see subsequent keys) AND on
        // `'locked'` (deterministic deny; further keystrokes can't
        // change the verdict, and refiring the JIT chain per key at
        // OS autorepeat would exhaust GitHub's install rate limit).
        // Cancelled / network-error / transient-failure paths return
        // undefined and KEEP the listener so the user can retry by
        // typing again.
        if (result === 'success' || result === 'locked') {
          host.removeEventListener('keydown', keydownHandler);
        }
      })();
    };
    host.addEventListener('keydown', keydownHandler);
    // Issue #91 / AC 5.3 — Post-auth restore. If sessionStorage carries
    // the pending-edit flag set by attemptEditAction's pre-redirect
    // path, the user just returned from the auth round-trip; auto-flip
    // into edit mode and surface the "your edit was paused" prompt.
    // Consumed (cleared) here so reloads of the same tab don't
    // re-trigger. Only fires on a successful mountViewer — a failed
    // mount falls through to renderViewerError below and the flag
    // persists so the user can retry on next load.
    let pending: string | null = null;
    try {
      pending = sessionStorage.getItem(PENDING_EDIT_KEY);
    } catch {
      // sessionStorage may throw in private-mode / quota-full edge
      // cases — treat as no pending flag.
    }
    if (pending !== null) {
      try {
        sessionStorage.removeItem(PENDING_EDIT_KEY);
      } catch {
        /* swallow */
      }
      // Issue #91 fix-loop-1 / fix #5 — pass isRestore so a 401 here
      // (user clicked back from GitHub without auth'ing) renders an
      // "auth cancelled" banner instead of looping back through
      // /auth/start.
      const result = await attemptEditAction(host, { isRestore: true });
      // Issue #91 fix-loop-2 / fix #2 — only render the post-auth
      // prompt when the restore actually succeeded (user signed in
      // and has push access). On 'locked' / 'cancelled' the relevant
      // banner already rendered inside attemptEditAction; stacking
      // "you're signed in, please redo" on top would contradict it.
      if (result === 'success') {
        renderPostAuthPrompt(host);
      }
      // Iter-3 fix #2 — self-remove on success (editor is editable)
      // AND on locked (no point firing the JIT chain again on every
      // keystroke when the verdict is deterministic). Cancelled +
      // transient-failure paths keep the listener for retry.
      if (result === 'success' || result === 'locked') {
        host.removeEventListener('keydown', keydownHandler);
      }
    }
    return;
  }
  renderViewerError(host, result);
}

// Issue #91 / AC 5.3 — Post-auth prompt. The MVP doesn't capture the
// original edit intent (transient keystroke, formatting click target),
// so the user has to redo the action. The prompt lets them know that.
// Form factor pinned by the test (data-testid + at least one DOM
// node); copy / styling is product discretion.
// Issue #91 fix-loop-2 / fix #1 — Filter for content-modifying keys.
// The keydown handler installed in bootstrapWeb fires for printable
// characters and content-altering control keys (Backspace / Delete /
// Enter) but skips pure modifiers, navigation, focus, and Escape so
// keyboard users can still navigate the read-only viewer without
// triggering the JIT auth flow on every arrow key.
function isContentModifyingKey(event: KeyboardEvent): boolean {
  if (event.ctrlKey || event.metaKey || event.altKey) return false;
  if (
    event.key === 'Backspace' ||
    event.key === 'Delete' ||
    event.key === 'Enter'
  ) {
    return true;
  }
  // Single-char keys are printable text. Multi-char keys are named
  // (`ArrowLeft`, `Tab`, `Shift`, `Escape`, `F5`, etc.) — not text.
  if (event.key.length === 1) return true;
  return false;
}

function renderPostAuthPrompt(host: HTMLElement): void {
  if (typeof document === 'undefined') return;
  if (document.querySelector('[data-testid="post-auth-prompt"]')) return;
  const prompt = document.createElement('div');
  prompt.setAttribute('data-testid', 'post-auth-prompt');
  prompt.setAttribute('role', 'status');
  prompt.className = 'hashly-post-auth-prompt';
  prompt.textContent =
    "You're signed in. Please redo your edit — we couldn't restore the original action.";
  // Issue #91 fix-loop-1 / fix #4 — prompt ABOVE the editor body so
  // the user sees it without scrolling past the rendered markdown.
  host.prepend(prompt);
}

if (import.meta.env.MODE !== 'test') {
  bootstrap();
}
